import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import {
  BarChart3, TrendingUp, Gauge, Sparkles, Target, ShieldCheck,
  ArrowRight, Loader2, Check, X, AlertTriangle, Brain, FileText,
  Clock, MapPin, Euro, Percent, Copy, CheckCircle2, Lightbulb,
  Archive, Swords, Plus, Search, Tag, Trash2, Edit, Wand2, BookOpen, Trophy,
  Building2, BookMarked, Users, Signal, ClipboardCheck, PhoneCall
} from "lucide-react";
import { toast } from "sonner";
import { ESTADO_LABELS, ESTADO_CLS, fmtEur, Field, Kpi } from "./shared";

export default function HistoricTenderDB() {
  const [data, setData] = useState(null);
  const [council, setCouncil] = useState("");
  const [winner, setWinner] = useState("");
  const [year, setYear] = useState("");
  const [docType, setDocType] = useState("");
  const [minMm, setMinMm] = useState(0); // Iter283 · filtro de importe persistente
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (council) params.append("council", council);
    if (winner)  params.append("winner", winner);
    if (year)    params.append("year", year);
    if (docType) params.append("doc_type", docType);
    if (minMm)   params.append("min_mm", minMm);
    try {
      const r = await axios.get(`${API}/tender360/historic-tenders?${params.toString()}`);
      setData(r.data);
    } catch { toast.error("Error cargando histórico"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [council, winner, year, docType, minMm]);
  // P0-04 (plan cierre POC/Comité 12/10/2026) · idem BidAIKnowledge — maestro/
  // detalle, auto-selecciona el item al llegar con "?open=".
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("open");
    if (id && data?.items) {
      const it = data.items.find((x) => x.id === id);
      if (it) setSelected(it);
    }
  }, [data]);

  const generateSummary = async (tid) => {
    setLoadingAI(true);
    try {
      const res = await postAiJob(`${API}/tender360/historic-tenders/${tid}/generate-summary`);
      toast.success("Resumen Pando generado");
      setSelected({ ...(selected || {}), ...res });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Error Pando"); }
    finally { setLoadingAI(false); }
  };

  const deleteItem = async (tid) => {
    if (!window.confirm("¿Eliminar este pliego histórico?")) return;
    try { await axios.delete(`${API}/tender360/historic-tenders/${tid}`); toast.success("Eliminado"); load(); setSelected(null); }
    catch { toast.error("Error al eliminar"); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="historic-tender-db">
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Pliegos capturados"   val={data.kpis.total} />
        <Kpi label="Cerrados/Adjudicados" val={data.kpis.closed} />
        <Kpi label="Ganadores únicos"     val={data.kpis.unique_winners} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <input placeholder="Ayuntamiento" value={council} onChange={e=>setCouncil(e.target.value)}
               className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs" data-testid="hist-council-input"/>
        <input placeholder="Ganador" value={winner} onChange={e=>setWinner(e.target.value)}
               className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs" data-testid="hist-winner-input"/>
        <input placeholder="Año" value={year} onChange={e=>setYear(e.target.value)} type="number"
               className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs w-20"/>
        <select value={docType} onChange={e=>setDocType(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white">
          <option value="">Todos los tipos</option>
          <option>RSU</option><option>Limpieza viaria</option><option>ORA</option><option>Mixto</option><option>Otro</option>
        </select>
        {/* Iter283 · Importe ≥ X M€ — filtro persistente pedido por el usuario */}
        <div
          data-testid="hist-filter-importe-wrap"
          title="Filtra el histórico por importe"
          className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
            minMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
          }`}>
          <span className={minMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
          <input
            type="range" min={0} max={30} step={1}
            value={minMm}
            onChange={(e) => setMinMm(Number(e.target.value))}
            data-testid="hist-filter-importe"
            className="accent-sky-500 w-20"
          />
          <span className={`font-mono w-12 text-right ${minMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{minMm}M€</span>
        </div>
        <div className="flex-1"/>
        <button onClick={()=>setShowForm(true)}
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#006528]"
                data-testid="hist-add-btn">
          <Plus size={13}/> Añadir pliego
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {(data.items || []).map(it => (
              <button key={it.id} onClick={()=>setSelected(it)}
                      className={`w-full text-left p-3 hover:bg-gray-50 ${selected?.id===it.id ? "bg-gray-50" : ""}`}
                      data-testid={`hist-item-${it.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-gray-900">{it.council}</div>
                  <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{it.year}</span>
                </div>
                <div className="text-xs text-gray-600 mt-0.5 truncate">{it.title}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px]">
                  <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">{it.doc_type || "—"}</span>
                  {it.winner_final && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded truncate max-w-[140px]">{it.winner_final}</span>}
                  {it.importe_eur && <span className="text-gray-500 font-mono">{fmtEur(it.importe_eur)}</span>}
                </div>
              </button>
            ))}
            {!data.items?.length && <div className="p-6 text-center text-gray-400 text-xs">Sin pliegos con ese filtro</div>}
          </div>
        </div>

        <div className="lg:col-span-3">
          {!selected
            ? <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-xs">Selecciona un pliego para ver el detalle</div>
            : <HistoricDetail item={selected} onGenerate={()=>generateSummary(selected.id)} loading={loadingAI} onDelete={()=>deleteItem(selected.id)} />}
        </div>
      </div>

      {showForm && <HistoricForm onClose={()=>{setShowForm(false);load();}} />}
    </div>
  );
}

function HistoricDetail({ item, onGenerate, loading, onDelete }) {
  const s = item.ai_summary;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="hist-detail">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
        <div>
          <div className="text-sm font-bold text-gray-900">{item.title}</div>
          <div className="text-xs text-gray-500 mt-0.5">{item.council} · {item.year} · {item.doc_type}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onGenerate} disabled={loading}
                  className="bg-[#007934] text-white text-xs font-semibold px-2 py-1.5 rounded hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1"
                  data-testid="hist-generate-summary-btn">
            {loading ? <Loader2 className="animate-spin" size={12}/> : <Wand2 size={12}/>}
            {s ? "Regenerar Pando" : "Resumir con Pando"}
          </button>
          <button onClick={onDelete} className="text-xs text-rose-600 hover:bg-rose-50 p-1.5 rounded" data-testid="hist-delete-btn">
            <Trash2 size={12}/>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><span className="text-gray-500">Ganador final:</span> <span className="font-semibold">{item.winner_final || "—"}</span></div>
        <div><span className="text-gray-500">Ganador provisional:</span> <span className="font-semibold">{item.winner_initial || "—"}</span></div>
        <div><span className="text-gray-500">Importe:</span> <span className="font-mono font-semibold">{fmtEur(item.importe_eur)}</span></div>
        <div><span className="text-gray-500">Estado:</span> <span className="font-semibold">{item.bid_status}</span></div>
      </div>

      {(item.participants || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">Participantes</div>
          <div className="flex flex-wrap gap-1">
            {item.participants.map((p,i) => <span key={i} className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-semibold">{p}</span>)}
          </div>
        </div>
      )}

      {item.scores && (
        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">Scores técnicos</div>
          <div className="space-y-1">
            {Object.entries(item.scores).map(([who, sc]) => (
              <div key={who}>
                <div className="flex items-center justify-between text-[11px]"><span>{who}</span><span className="font-mono font-bold">{sc}</span></div>
                <div className="w-full bg-gray-100 rounded-full h-1"><div className="bg-[#007934] h-full rounded-full" style={{width:`${Math.min(100,sc)}%`}}/></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {item.notes && <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded"><strong>Notas:</strong> {item.notes}</div>}

      {s && (
        <div className="border-t border-gray-100 pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-bold text-[#007934] tracking-wide">Resumen Pando estructurado</div>
            {item.requisitos_evidence_status === "con_evidencia_verificada"
              ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">Con evidencia verificada</span>
              : <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold" data-testid="hist-sin-evidencia-badge">Inferido del texto pegado — sin evidencia documental</span>}
          </div>
          <p className="text-xs text-gray-800">{s.resumen}</p>
          {s.requisitos_it?.length > 0 && (
            <div><div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mt-2 mb-1">Requisitos IT</div>
              <ul className="list-disc pl-4 text-xs text-gray-700 space-y-0.5">{s.requisitos_it.map((r,i)=><li key={i}>{r}</li>)}</ul></div>
          )}
          {s.certificaciones_obligatorias?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {s.certificaciones_obligatorias.map((c,i)=><span key={i} className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-semibold">{c}</span>)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
            {s.scoring_tecnico_vs_economico && <div><span className="text-gray-500">Scoring T/E:</span> <strong>{s.scoring_tecnico_vs_economico}</strong></div>}
            {s.duracion_anios && <div><span className="text-gray-500">Duración:</span> <strong>{s.duracion_anios} años</strong></div>}
            {s.subrogacion_personal !== undefined && <div><span className="text-gray-500">Subrogación:</span> <strong>{s.subrogacion_personal ? "Sí" : "No"}</strong></div>}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoricForm({ onClose }) {
  const [form, setForm] = useState({
    council: "", year: new Date().getFullYear(), doc_type: "RSU", title: "",
    bid_status: "closed", winner_initial: "", winner_final: "", participants: "",
    importe_eur: "", notes: "", raw_text: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.council) { toast.error("Indica el ayuntamiento"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tender360/historic-tenders`, {
        ...form,
        year: parseInt(form.year) || new Date().getFullYear(),
        importe_eur: form.importe_eur ? parseFloat(form.importe_eur) : null,
        participants: form.participants ? form.participants.split(",").map(s=>s.trim()).filter(Boolean) : [],
      });
      toast.success("Pliego añadido"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Nuevo pliego histórico</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Ayuntamiento *"><input value={form.council} onChange={e=>setForm({...form,council:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Año"><input type="number" value={form.year} onChange={e=>setForm({...form,year:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Tipo"><select value={form.doc_type} onChange={e=>setForm({...form,doc_type:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white"><option>RSU</option><option>Limpieza viaria</option><option>ORA</option><option>Mixto</option><option>Otro</option></select></Field>
          <Field label="Estado"><select value={form.bid_status} onChange={e=>setForm({...form,bid_status:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1 bg-white"><option>open</option><option>closed</option><option>awarded</option></select></Field>
          <Field label="Título" span={2}><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Ganador provisional"><input value={form.winner_initial} onChange={e=>setForm({...form,winner_initial:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Adjudicatario final"><input value={form.winner_final} onChange={e=>setForm({...form,winner_final:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Importe (€)"><input type="number" value={form.importe_eur} onChange={e=>setForm({...form,importe_eur:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Participantes (coma-separado)"><input value={form.participants} onChange={e=>setForm({...form,participants:e.target.value})} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Notas" span={2}><textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} className="w-full border border-gray-200 rounded px-2 py-1"/></Field>
          <Field label="Texto del pliego (para Pando)" span={2}><textarea value={form.raw_text} onChange={e=>setForm({...form,raw_text:e.target.value})} rows={4} className="w-full border border-gray-200 rounded px-2 py-1 font-mono text-[10px]"/></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1">
            {saving && <Loader2 className="animate-spin" size={12}/>} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

